import { useTranslation } from 'react-i18next';
import { EyeOffIcon, XIcon } from 'lucide-react';
import { cn } from '@noor/lib';
import {
  OFFER_NOT_WORKING_REASON_ID,
  type OfferFilters,
  type OfferStat,
} from '@/entities/order';
import { toneFill, toneInk } from '../lib/offer-colors';

interface OfferStatsBarProps {
  stats: OfferStat[];
  filters: OfferFilters;
  onToggle: (filterKey: string) => void;
  onClear: () => void;
}

/**
 * cpanel's footer of per-reason counts, promoted to a legend that doubles as
 * the filter: a pressed chip highlights its offers on the map and dims the
 * rest (the "not working" bounces are hidden until their chip is pressed).
 */
export function OfferStatsBar({ stats, filters, onToggle, onClear }: OfferStatsBarProps) {
  const { t } = useTranslation('orders');
  const anyActive = Object.values(filters).some(Boolean);

  return (
    <div className="flex items-start gap-2">
      <div
        role="group"
        aria-label={t('offers.filters')}
        className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pb-1 sm:max-h-28 sm:flex-wrap sm:overflow-x-visible sm:overflow-y-auto sm:pb-0"
      >
        {stats.map((stat) => {
          const active = Boolean(filters[stat.filterKey]);
          const hiddenOnMap = stat.filterKey === OFFER_NOT_WORKING_REASON_ID && !active;
          const label = stat.reason
            ? t(`offers.${stat.reason.labelKey}`)
            : t('offers.reason.ReasonNull');
          return (
            <button
              key={stat.key}
              type="button"
              aria-pressed={active}
              title={hiddenOnMap ? `${label} — ${t('offers.hiddenHint')}` : label}
              onClick={() => onToggle(stat.filterKey)}
              className={cn(
                'inline-flex h-8 max-w-full shrink-0 cursor-pointer items-center gap-2 rounded-full border bg-card py-0.5 pr-3 pl-1 text-xs text-foreground transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/40',
                active ? 'border-primary bg-accent' : 'border-border',
                hiddenOnMap && 'text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'grid h-6 min-w-6 place-items-center rounded-full px-1.5 font-semibold tabular-nums',
                  !stat.reason && 'bg-muted text-foreground',
                )}
                style={
                  stat.reason
                    ? {
                        backgroundColor: toneFill(stat.reason.tone),
                        color: toneInk(stat.reason.tone),
                      }
                    : undefined
                }
              >
                {stat.count}
              </span>
              <span className="truncate">{label}</span>
              {hiddenOnMap ? <EyeOffIcon className="size-3.5 shrink-0" /> : null}
            </button>
          );
        })}
      </div>
      {/* Always mounted (hidden when idle) in its own slot, so toggling a filter
          never reflows the legend or shoves the map down under the cursor. */}
      <button
        type="button"
        onClick={onClear}
        tabIndex={anyActive ? 0 : -1}
        aria-hidden={!anyActive}
        className={cn(
          'inline-flex h-8 shrink-0 cursor-pointer items-center gap-1 rounded-full px-2.5 text-xs text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40',
          !anyActive && 'hidden sm:invisible sm:inline-flex',
        )}
      >
        <XIcon className="size-3.5" />
        {t('offers.clearFilters')}
      </button>
    </div>
  );
}
